import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import MobileWrapper from "../../components/MobileWrapper";
import { motion } from "framer-motion";
import { v4 } from "uuid";
import toast from "react-hot-toast";
import {
  addNewDoc,
  getAllDocsSnap,
  updateDocById,
} from "../../utils/firebase/Firebase.utils";
import {
  FiAlertTriangle,
  FiCalendar,
  FiClock,
  FiEye,
  FiFileText,
  FiFilter,
  FiMapPin,
  FiPlus,
  FiSearch,
  FiShield,
  FiUser,
  FiX,
} from "react-icons/fi";
import { convertTimeStamptoDate } from "../../utils/EleghoUtils";

function SecurityReporting() {
  const currentUser = useSelector((state) => state.userObject);
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logId, setLogId] = useState(v4().slice(0, 8).toUpperCase());
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    title: "",
    location: "",
    category: "general",
    description: "",
    dateTime: "",
  });

  // Color scheme
  const eleghoBlue = "#363062";
  const eleghoGreen = "#8FCD00";

  // Set current date and time as default
  useEffect(() => {
    const now = new Date();
    const localDateTime = new Date(now - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);

    setFormData((prevData) => ({
      ...prevData,
      dateTime: localDateTime,
    }));
  }, []);

  // Fetch all security logs
  useEffect(() => {
    const unsubscribe = getAllDocsSnap("securityLogs", (logData) => {
      // Sort by dateTime (newest first)
      const sortedLogs = logData.sort(
        (a, b) => new Date(b.dateTime) - new Date(a.dateTime)
      );
      setLogs(sortedLogs);
      setLoading(false);
    });

    // Cleanup subscription on component unmount
    return () => unsubscribe && unsubscribe();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const toggleForm = () => {
    setShowForm(!showForm);
    // Reset form when opening
    if (!showForm) {
      const now = new Date();
      const localDateTime = new Date(now - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);

      setFormData({
        title: "",
        location: "",
        category: "general",
        description: "",
        dateTime: localDateTime,
      });
      // Generate new log ID
      setLogId(v4().slice(0, 8).toUpperCase());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Please enter a title for the security log");
      return;
    }

    if (!formData.description.trim()) {
      toast.error("Please enter a description for the security log");
      return;
    }

    try {
      setIsSubmitting(true);

      // Create the log data object
      const logData = {
        ...formData,
        logId,
        createdAt: new Date().toISOString(),
        createdBy: currentUser?.email || "Unknown",
        createdByName: currentUser?.displayName || "Unknown",
        status: "active",
      };

      // Save to Firestore
      const docRef = await addNewDoc("securityLogs", logData);

      if (docRef) {
        toast.success("Security log submitted successfully");
        // Reset form and close it
        setFormData({
          title: "",
          location: "",
          category: "general",
          description: "",
          dateTime: "",
        });
        setShowForm(false);
      } else {
        throw new Error("Failed to create document");
      }
    } catch (error) {
      console.error("Error submitting security log:", error);
      toast.error(`An error occurred: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusColor = (category) => {
    switch (category.toLowerCase()) {
      case "critical":
        return "bg-red-100 text-red-700";
      case "incident":
        return "bg-orange-100 text-orange-700";
      case "suspicious":
        return "bg-amber-100 text-amber-700";
      case "routine":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getCategoryIcon = (category) => {
    switch (category.toLowerCase()) {
      case "critical":
        return <FiAlertTriangle className="w-3.5 h-3.5" />;
      case "incident":
        return <FiAlertTriangle className="w-3.5 h-3.5" />;
      case "suspicious":
        return <FiEye className="w-3.5 h-3.5" />;
      case "routine":
        return <FiFileText className="w-3.5 h-3.5" />;
      default:
        return <FiFileText className="w-3.5 h-3.5" />;
    }
  };

  const filteredLogs = logs
    .filter((log) => {
      if (filter === "all") return true;
      return log.category.toLowerCase() === filter.toLowerCase();
    })
    .filter((log) => {
      if (!searchQuery.trim()) return true;

      const query = searchQuery.toLowerCase().trim();
      return (
        log.title?.toLowerCase().includes(query) ||
        log.location?.toLowerCase().includes(query) ||
        log.description?.toLowerCase().includes(query)
      );
    });

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedLog(null);
  };

  // Get counts for each category
  const getCategoryCount = (category) => {
    return logs.filter(
      (log) => log.category?.toLowerCase() === category.toLowerCase()
    ).length;
  };

  return (
    <MobileWrapper label="Security Reporting">
      <div className="min-h-screen bg-gray-50 p-4 pb-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 bg-[#363062]/5 p-3 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FiShield className="text-white text-sm" />
            </div>
            <div>
              <h1 className="text-xs font-medium text-[#363062]">
                Security Reporting
              </h1>
              <p className="text-xs text-gray-500">
                Log and track security issues
              </p>
            </div>
          </div>
          <button
            onClick={toggleForm}
            className="flex items-center px-3 py-1.5 text-xs font-medium text-white rounded-md bg-[#8FCD00] shadow-sm hover:bg-[#7db700] transition-colors"
          >
            {showForm ? (
              <>
                <FiX className="w-3 h-3 mr-1.5" />
                Cancel
              </>
            ) : (
              <>
                <FiPlus className="w-3 h-3 mr-1.5" />
                New Log
              </>
            )}
          </button>
        </div>

        {/* Form Section */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6"
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Form Header */}
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-sm font-medium text-[#363062]">
                    New Security Log
                  </h2>
                  <div className="text-xs text-gray-500">Ref: {logId}</div>
                </div>

                {/* Basic Info */}
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Brief title of the issue"
                      required
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062] text-xs"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">
                      Location
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Where the incident occurred"
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062] text-xs"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-700 mb-1 block">
                        Date & Time *
                      </label>
                      <input
                        type="datetime-local"
                        name="dateTime"
                        value={formData.dateTime}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062] text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-700 mb-1 block">
                        Category
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062] text-xs"
                      >
                        <option value="general">General</option>
                        <option value="routine">Routine</option>
                        <option value="suspicious">Suspicious Activity</option>
                        <option value="incident">Incident</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">
                      Description *
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Detailed description of the security issue"
                      required
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062] text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex space-x-3">
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isSubmitting}
                  className="flex-1 py-2.5 rounded-md text-white text-xs font-medium flex items-center justify-center gap-2"
                  style={{ backgroundColor: "#363062" }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Security Log"
                  )}
                </motion.button>
                <motion.button
                  type="button"
                  onClick={toggleForm}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-4 py-2.5 border rounded-md text-xs"
                  style={{ borderColor: "#ddd", color: "#666" }}
                  disabled={isSubmitting}
                >
                  Cancel
                </motion.button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Search and Filters */}
        {!showForm && (
          <>
            {/* Search Bar */}
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FiSearch className="w-3.5 h-3.5 text-gray-500" />
              </div>
              <input
                type="text"
                className="block w-full p-2.5 pl-10 text-xs border rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
                placeholder="Search security logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ borderColor: "#E2E8F0" }}
              />
              {searchQuery && (
                <button
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                  onClick={() => setSearchQuery("")}
                >
                  <FiX className="w-3.5 h-3.5 text-gray-500" />
                </button>
              )}
            </div>

            {/* Filter Tabs */}
            <div className="mb-4 overflow-x-auto">
              <div className="flex space-x-2 pb-2">
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                    filter === "all"
                      ? "bg-[#363062] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  onClick={() => setFilter("all")}
                >
                  All ({logs.length})
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                    filter === "critical"
                      ? "bg-red-500 text-white"
                      : "bg-red-100 text-gray-700 hover:bg-red-200"
                  }`}
                  onClick={() => setFilter("critical")}
                >
                  Critical ({getCategoryCount("critical")})
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                    filter === "incident"
                      ? "bg-orange-500 text-white"
                      : "bg-orange-100 text-gray-700 hover:bg-orange-200"
                  }`}
                  onClick={() => setFilter("incident")}
                >
                  Incident ({getCategoryCount("incident")})
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                    filter === "suspicious"
                      ? "bg-amber-500 text-white"
                      : "bg-amber-100 text-gray-700 hover:bg-amber-200"
                  }`}
                  onClick={() => setFilter("suspicious")}
                >
                  Suspicious ({getCategoryCount("suspicious")})
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                    filter === "routine"
                      ? "bg-blue-500 text-white"
                      : "bg-blue-100 text-gray-700 hover:bg-blue-200"
                  }`}
                  onClick={() => setFilter("routine")}
                >
                  Routine ({getCategoryCount("routine")})
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                    filter === "general"
                      ? "bg-gray-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  onClick={() => setFilter("general")}
                >
                  General ({getCategoryCount("general")})
                </button>
              </div>
            </div>

            {/* Security Log Cards */}
            <div className="mt-4 space-y-3">
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#363062]"></div>
                    <p className="mt-2 text-sm text-[#363062]">
                      Loading security logs...
                    </p>
                  </div>
                </div>
              ) : filteredLogs.length === 0 ? (
                <div className="text-center py-8 bg-white rounded-lg border border-gray-100 shadow-sm">
                  <div className="p-3 bg-[#363062]/10 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                    <FiShield className="text-[#363062] text-lg" />
                  </div>
                  <h3 className="text-sm font-medium text-[#363062] mb-1">
                    {logs.length === 0
                      ? "No Security Logs"
                      : "No Matching Results"}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {logs.length === 0
                      ? "No security logs have been added yet"
                      : "Try adjusting your filters or search terms"}
                  </p>
                </div>
              ) : (
                filteredLogs.map((log) => (
                  <motion.div
                    key={log.id}
                    whileHover={{ scale: 1.01 }}
                    className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden cursor-pointer transition-all hover:shadow-md"
                    onClick={() => handleViewDetails(log)}
                  >
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="text-sm font-medium text-[#363062]">
                          {log.title}
                        </div>
                        <div
                          className={`flex items-center px-2 py-1 rounded-full text-xs font-medium gap-1.5 ${getStatusColor(
                            log.category
                          )}`}
                        >
                          {getCategoryIcon(log.category)}
                          <span>{log.category}</span>
                        </div>
                      </div>

                      <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                        {log.description}
                      </p>

                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mt-3">
                        <div className="flex items-center gap-1.5">
                          <FiCalendar className="text-gray-400 flex-shrink-0" />
                          <span>{formatDate(log.dateTime).split(",")[0]}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <FiClock className="text-gray-400 flex-shrink-0" />
                          <span>{formatDate(log.dateTime).split(",")[1]}</span>
                        </div>
                        {log.location && (
                          <div className="flex items-center gap-1.5">
                            <FiMapPin className="text-gray-400 flex-shrink-0" />
                            <span>{log.location}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <FiUser className="text-gray-400 flex-shrink-0" />
                          <span>{log.createdByName || log.createdBy}</span>
                        </div>
                      </div>
                    </div>
                    <div className="py-2 px-4 bg-gray-50 border-t border-gray-100">
                      <div className="flex justify-end">
                        <button className="px-2 py-1 text-xs text-[#363062] bg-[#363062]/10 rounded-md hover:bg-[#363062]/20 transition flex items-center">
                          <FiEye className="mr-1 w-3 h-3" />
                          View Details
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </>
        )}

        {/* Log Details Modal */}
        {showDetails && selectedLog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-auto relative">
              <button
                className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 p-2 z-10"
                onClick={closeDetails}
              >
                <FiX className="h-5 w-5" />
              </button>

              <div className="p-5">
                <div className="mb-4">
                  <h2 className="text-lg font-medium text-[#363062] mb-1">
                    {selectedLog.title}
                  </h2>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium gap-1 ${getStatusColor(
                        selectedLog.category
                      )}`}
                    >
                      {getCategoryIcon(selectedLog.category)}
                      {selectedLog.category}
                    </span>
                    <span className="text-xs text-gray-500">
                      Ref: {selectedLog.logId}
                    </span>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="text-sm font-medium text-[#363062] mb-1">
                    Description
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-line">
                    {selectedLog.description}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500">
                      Date
                    </div>
                    <div className="text-sm">
                      {formatDate(selectedLog.dateTime).split(",")[0]}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500">
                      Time
                    </div>
                    <div className="text-sm">
                      {formatDate(selectedLog.dateTime).split(",")[1]}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500">
                      Location
                    </div>
                    <div className="text-sm">
                      {selectedLog.location || "Not specified"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500">
                      Reported By
                    </div>
                    <div className="text-sm">
                      {selectedLog.createdByName || selectedLog.createdBy}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500">
                      Reported On
                    </div>
                    <div className="text-sm">
                      {formatDate(selectedLog.createdAt)}
                    </div>
                  </div>
                </div>

                <button
                  onClick={closeDetails}
                  className="w-full py-2 mt-2 bg-[#363062] text-white rounded-md text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MobileWrapper>
  );
}

export default SecurityReporting;
